import { Button } from '@tempo-io/sui-button';
import { Spinner } from '@tempo-io/sui-spinner';
import { TableBody, TableHead, TableHeader, TableRoot, TableRow } from '@tempo-io/sui-table';
import type { TFunction } from 'i18next';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { IntegrationSyncFailedItem } from '@/lib/api/work-integrations/syncFailures';
import type { SyncFailureRowVariant } from './SyncFailureRow';
import { SyncFailureRow } from './SyncFailureRow';
import {
  COL_AFFECTED,
  COL_DIRECTION,
  COL_ITEM,
  COL_LOOP,
  COL_MESSAGE,
  COL_TYPE,
} from './syncFailureColumns';

type ColumnId = 'loop' | 'item' | 'direction' | 'type' | 'message' | 'affectedFields';

const COLUMN_CLASSES: Record<ColumnId, string> = {
  loop: COL_LOOP,
  item: COL_ITEM,
  direction: COL_DIRECTION,
  type: COL_TYPE,
  message: COL_MESSAGE,
  affectedFields: COL_AFFECTED,
};

const COLUMNS_BY_VARIANT: Record<SyncFailureRowVariant, readonly ColumnId[]> = {
  integration: ['loop', 'item', 'direction', 'type', 'message', 'affectedFields'],
  item: ['direction', 'type', 'message', 'affectedFields'],
};

const getColumnLabel = (t: TFunction, id: ColumnId): string =>
  t(($) => $.common.settings['work-integrations'].detail.syncFailures.columns[id]);

export const SYNC_FAILURES_PAGE_SIZE = 20;

export type SyncFailuresQueryResult = {
  data: { items: IntegrationSyncFailedItem[]; totalCount: number } | undefined;
  isLoading: boolean;
  isError: boolean;
  isRefetching: boolean;
  refetch: () => unknown;
};

type Props = {
  queryResult: SyncFailuresQueryResult;
  page: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  variant: SyncFailureRowVariant;
  tableTestId: string;
};

export function SyncFailuresTable({
  queryResult,
  page,
  onPageChange,
  pageSize = SYNC_FAILURES_PAGE_SIZE,
  variant,
  tableTestId,
}: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch, isRefetching } = queryResult;

  const failures = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const isOnStalePage = Boolean(data && page > 0 && page >= totalPages);

  useEffect(() => {
    if (isOnStalePage) onPageChange(0);
  }, [isOnStalePage, onPageChange]);

  if (isLoading || isOnStalePage) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-6">
        <span className="text-sm text-text-error">
          {t(($) => $.common.settings['work-integrations'].detail.syncFailures.error)}
        </span>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isRefetching}>
          {isRefetching && <Spinner size={14} color="inherit" />}
          {t(($) => $.common.settings['work-integrations'].detail.syncFailures.retry)}
        </Button>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <span className="text-sm text-text-neutral-secondary">
          {t(($) => $.common.settings['work-integrations'].detail.syncFailures.empty)}
        </span>
      </div>
    );
  }

  const columns = COLUMNS_BY_VARIANT[variant];

  return (
    <div className="flex flex-col gap-3">
      <div
        className="overflow-y-hidden overflow-x-auto rounded-lg border border-border-neutral-default"
        data-testid={tableTestId}
      >
        <TableRoot>
          <TableHeader>
            <TableRow>
              {columns.map((id) => (
                <TableHead
                  key={id}
                  className={`text-xs font-medium text-text-neutral-secondary ${COLUMN_CLASSES[id]}`}
                >
                  {getColumnLabel(t, id)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {failures.map((failure) => (
              <SyncFailureRow
                key={`${failure.syncHistoryId ?? 'none'}-${failure.sourceItemKey ?? failure.itemId ?? 'unknown'}-${failure.failedAt}`}
                failure={failure}
                variant={variant}
              />
            ))}
          </TableBody>
        </TableRoot>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
          >
            {t(($) => $.common.settings['work-integrations'].detail.syncFailures.pagination.prev)}
          </Button>
          <span className="text-xs text-text-neutral-secondary px-1">
            {t(($) => $.common.settings['work-integrations'].detail.syncFailures.pagination.page, {
              page: page + 1,
              totalPages,
            })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
          >
            {t(($) => $.common.settings['work-integrations'].detail.syncFailures.pagination.next)}
          </Button>
        </div>
      )}
    </div>
  );
}
